<template>
  <div class="w-full overflow-hidden">
    <div class="p-4">
      <span class="text-lg font-semibold">Reimbursements</span>
    </div>

    <div class="w-full overflow-x-auto">
      <v-data-table
        :headers="headers"
        :items="reimbursements"
        :loading="loading"
        :items-per-page="10"
        class="table-radius"
        item-key="_id"
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="`tooltip-${index}`"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template #[`item.user`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.user.firstName || '' }} {{ item.user.lastName || '' }}
          </td>
        </template>
        <template #[`item.billNumber`]="{ item }">
          <td class="px-4 py-4 table-font">{{ item.billNumber }}</td>
        </template>
        <template #[`item.claimAmount`]="{ item }">
          <td class="px-4 py-4 table-font">
            <span class="font-bold text-slate-900 block">₹{{ item.claimAmount }}</span>
            <span v-if="item.travelDetails && item.travelDetails.distanceKm !== undefined && item.travelDetails.distanceKm !== null"
              class="text-[11px] text-gray-500 block leading-tight">
              Claim Dist: <strong>{{ item.travelDetails.distanceKm }} km</strong>
            </span>
            <!-- System GPS calculated metrics -->
            <span v-if="item.travelDetails && (item.travelDetails.systemDistanceKm !== undefined || item.travelDetails.systemCalculatedAmount !== undefined)"
              class="text-[10px] text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded border border-emerald-200 block mt-0.5 w-max">
              GPS: {{ item.travelDetails.systemDistanceKm || 0 }} km (₹{{ item.travelDetails.systemCalculatedAmount || 0 }})
            </span>
          </td>
        </template>

        <template #[`item.billDate`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ $dayjs(item.billDate).format('DD/MM/YYYY') }}
          </td>
        </template>

        <template #[`item.month`]="{ item }">
          <td class="px-4 py-4 table-font">{{ item.month }} {{ item.year }}</td>
        </template>

        <template #[`item.reimbursementType`]="{ item }">
          <td class="px-4 py-4 table-font">
            <v-chip small label color="blue" text-color="white">
              {{ item.reimbursementTypeName || item.reimbursementType?.type || item.reimbursementType?.componentName || item.reimbursementType || '-' }}
            </v-chip>
            <div v-if="item.travelDetails && item.travelDetails.mode" class="text-[11px] text-gray-500 mt-1 font-medium flex items-center gap-1">
              <v-icon x-small color="grey">mdi-car</v-icon>
              <span>{{ item.travelDetails.mode }}</span>
              <span v-if="item.travelDetails.distanceKm">• {{ item.travelDetails.distanceKm }} km</span>
            </div>
          </td>
        </template>

        <template #[`item.comments`]="{ item }">
          <td class="px-4 font-semibold">
            <div class="flex items-center gap-2">
              <p class="ellipsisClass w-32 mb-0" :title="Array.isArray(item.comments) && item.comments.length > 0 ? item.comments[item.comments.length - 1].message : (item.comments || '-')">
                {{
                  Array.isArray(item.comments) && item.comments.length > 0
                    ? item.comments[item.comments.length - 1].message
                    : (item.comments || '-')
                }}
              </p>
              <v-btn icon x-small color="primary" @click="openCommentsDialog(item)" title="View Conversation & Reply">
                <v-badge :content="item.comments ? item.comments.length : 0" :value="item.comments && item.comments.length > 0" color="primary" overlap>
                  <v-icon small>mdi-message-text-outline</v-icon>
                </v-badge>
              </v-btn>
            </div>
          </td>
        </template>

        <template #[`item.attachedBill`]="{ item }">
          <td class="px-4 py-4 table-font">
            <v-btn
              v-if="item.attachedBill"
              icon
              @click="viewBill(item.attachedBill)"
              title="View Bill"
            >
              <v-icon>mdi-file-document</v-icon>
            </v-btn>
          </td>
        </template>
        <template #[`item.status`]="{ item }">
          <td class="px-4 py-4 table-font">
            <OrderStatuses
              :orderStatus="
                Array.isArray(item.approvals) && item.approvals.length > 0
                  ? item.approvals[item.approvals.length - 1].status
                  : 'Pending'
              "
              :statuses="getRequestStatuses(item)"
              @statusChange="(status) => initiateStatusUpdate(status, item._id)"
              v-iam-disabled="'alerts.alerts.write'"
            />
          </td>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="showBillDialog" max-width="800px">
      <v-card class="overflow-hidden">
        <v-card-title>
          <span class="text-md font-semibold">Bill Attachment</span>
        </v-card-title>
        <v-card-text>
          <v-img v-if="billImageUrl" :src="billImageUrl" contain max-height="600px"></v-img>
          <v-alert v-else type="warning"> Bill image not available </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="showBillDialog = false"> Close </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="commentDialog" max-width="500" persistent>
      <v-card class="overflow-hidden">
        <div class="text-lg flex justify-between items-center">
          <p class="p-2 mb-0">Add Comment</p>
          <v-icon @click="cancelStatusUpdate" class="cursor-pointer">mdi-close</v-icon>
        </div>
        <v-divider></v-divider>
        <div class="px-4 py-4">
          <v-textarea
            v-model="newComment"
            label="Comment (Optional)"
            outlined
            dense
            rows="3"
            hide-details
            class="mb-3"
          ></v-textarea>
        </div>
        <v-card-actions class="flex justify-end mb-2 px-4">
          <v-btn @click="cancelStatusUpdate" class="rounded-md mr-2 normal-case" outlined
            >Cancel</v-btn
          >
          <v-btn
            @click="confirmStatusUpdate"
            class="white--text rounded-md normal-case"
            :color="$vuetify.theme.currentTheme.primary"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Communication History & Direct Reply Dialog -->
    <v-dialog v-model="historyDialog" max-width="650" persistent scrollable>
      <v-card v-if="activeReimbursement" class="rounded-xl overflow-hidden flex flex-col max-h-[85vh]">
        <!-- Dialog Header -->
        <v-card-title class="px-5 py-3.5 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
          <div class="flex items-center gap-2">
            <v-icon color="primary" small>mdi-forum-outline</v-icon>
            <span class="text-sm font-bold text-slate-800">Claim Discussion & Audit Trail</span>
          </div>
          <v-btn icon small @click="closeCommentsDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Claim Summary Mini Card -->
        <div class="px-5 py-3 bg-blue-50/40 border-b border-blue-100/60 flex flex-wrap items-center justify-between gap-3 text-xs">
          <div>
            <div class="font-bold text-slate-900 text-sm">
              {{ activeReimbursement.reimbursementTypeName || activeReimbursement.reimbursementType?.type || activeReimbursement.reimbursementType || 'Claim' }}
              <span v-if="activeReimbursement.billNumber" class="text-slate-500 font-mono text-xs">#{{ activeReimbursement.billNumber }}</span>
            </div>
            <div class="text-slate-500 text-[11px] mt-0.5">
              Employee: <span class="font-medium text-slate-700">{{ activeReimbursement.user?.systemUserId?.firstName || activeReimbursement.user?.name || 'Employee' }}</span>
              • Month: <span class="font-medium text-slate-700">{{ activeReimbursement.month || '-' }} {{ activeReimbursement.year || '' }}</span>
            </div>
            <!-- Travel Details snippet if any -->
            <div v-if="activeReimbursement.travelDetails && activeReimbursement.travelDetails.mode" class="mt-1 space-y-0.5 text-[11px]">
              <div class="flex items-center gap-1.5 text-blue-700 font-medium">
                <v-icon x-small color="blue">mdi-car</v-icon>
                <span>Mode: {{ activeReimbursement.travelDetails.mode }}</span>
                <span v-if="activeReimbursement.travelDetails.pricePerKm">• Rate: ₹{{ activeReimbursement.travelDetails.pricePerKm }}/km</span>
                <span v-if="activeReimbursement.travelDetails.distanceKm !== undefined && activeReimbursement.travelDetails.distanceKm !== null">
                  • Claimed: <strong>{{ activeReimbursement.travelDetails.distanceKm }} km</strong>
                </span>
              </div>
              <div v-if="activeReimbursement.travelDetails.systemDistanceKm !== undefined || activeReimbursement.travelDetails.systemCalculatedAmount !== undefined"
                class="flex items-center gap-1.5 text-emerald-700 font-semibold">
                <v-icon x-small color="emerald">mdi-map-marker-distance</v-icon>
                <span>System Auto-calculated: {{ activeReimbursement.travelDetails.systemDistanceKm || 0 }} km</span>
                <span>(₹{{ activeReimbursement.travelDetails.systemCalculatedAmount || 0 }})</span>
              </div>
            </div>
          </div>
          <div class="text-right">
            <div class="text-[11px] text-slate-500 uppercase font-semibold">Claim Amount</div>
            <div class="text-base font-black text-slate-900">₹{{ activeReimbursement.claimAmount || 0 }}</div>
            <v-chip x-small label :color="getStatusChipColor(getActiveStatus(activeReimbursement))" text-color="white" class="mt-0.5 font-bold">
              {{ getActiveStatus(activeReimbursement) }}
            </v-chip>
          </div>
        </div>

        <!-- Scrollable Body with Approval Timeline and Comments Thread -->
        <v-card-text class="px-5 py-4 overflow-y-auto flex-grow space-y-4">
          <!-- Approval History Stepper / List -->
          <div v-if="activeReimbursement.approvals && activeReimbursement.approvals.length" class="bg-slate-50 rounded-xl p-3 border border-slate-200/80">
            <span class="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-2">Approval Timeline</span>
            <div class="space-y-2">
              <div v-for="(appr, aIdx) in activeReimbursement.approvals" :key="aIdx" class="flex items-center justify-between text-xs pb-1.5 border-b border-slate-200/50 last:border-0 last:pb-0">
                <div class="flex items-center gap-2">
                  <v-icon x-small :color="getStatusChipColor(appr.status)">mdi-check-circle-outline</v-icon>
                  <span class="font-bold text-slate-800">{{ appr.status }}</span>
                  <span v-if="appr.updatedByName" class="text-slate-500 text-[11px]">by {{ appr.updatedByName }}</span>
                </div>
                <span class="text-[10px] text-slate-400">{{ formatDialogDate(appr.updatedAt) }}</span>
              </div>
            </div>
          </div>

          <!-- Messages Stream -->
          <div>
            <span class="text-[11px] font-bold text-slate-600 uppercase tracking-wider block mb-2.5">Conversation History</span>
            <div v-if="!activeReimbursement.comments || !activeReimbursement.comments.length" class="text-center py-6 text-slate-400 text-xs">
              No comments or messages yet. Start the conversation below.
            </div>
            <div v-else class="space-y-3">
              <div
                v-for="(c, cIdx) in activeReimbursement.comments"
                :key="cIdx"
                :class="[
                  'p-3 rounded-2xl max-w-[85%] text-xs shadow-2xs',
                  c.commentedByRole === 'ADMIN'
                    ? 'ml-auto bg-blue-600 text-white rounded-br-xs'
                    : 'mr-auto bg-slate-100 text-slate-800 rounded-bl-xs border border-slate-200/70'
                ]"
              >
                <div class="flex items-center justify-between gap-3 text-[10px] mb-1 opacity-80">
                  <span class="font-bold flex items-center gap-1">
                    <span>{{ c.commentedByName || (c.commentedByRole === 'ADMIN' ? 'Admin' : 'Employee') }}</span>
                    <span
                      class="text-[9px] px-1 py-0.2 rounded font-semibold uppercase"
                      :class="c.commentedByRole === 'ADMIN' ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'"
                    >
                      {{ c.commentedByRole || 'NOTE' }}
                    </span>
                  </span>
                  <span>{{ formatDialogDate(c.commentedAt) }}</span>
                </div>
                <p class="mb-0 text-xs leading-relaxed whitespace-pre-wrap">{{ c.message }}</p>
              </div>
            </div>
          </div>
        </v-card-text>

        <!-- Message Input Box at Bottom -->
        <v-card-actions class="px-5 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
          <v-textarea
            v-model="replyMessage"
            placeholder="Write a message or reply to employee..."
            outlined
            dense
            rows="1"
            auto-grow
            hide-details
            class="text-xs"
            @keydown.enter.prevent="sendQuickReply"
          ></v-textarea>
          <v-btn
            color="primary"
            depressed
            :loading="replyLoading"
            :disabled="!replyMessage || !replyMessage.trim()"
            @click="sendQuickReply"
            class="rounded-xl px-4 normal-case font-bold"
          >
            Send
            <v-icon right small>mdi-send</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import OrderStatuses from '@/components/Workforce/InventoryManagement/OrderStatuses.vue'
export default {
  name: 'ReimbursementTable',
  props: {
    reimbursements: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
  },
  components: {
    OrderStatuses
  },
  data() {
    return {
      search: '',
      showBillDialog: false,
      billImageUrl: '',
      selectedReimbursement: null,
      statuses: ['Pending', 'Approved', 'Rejected', 'Review', 'Escalated', 'Resubmitted'],
      headers: [
        {
          text: 'Employee',
          align: 'start',
          value: 'user',
          sortable: false
        },
        {
          text: 'Bill Number',
          align: 'start',
          value: 'billNumber',
          sortable: false
        },
        {
          text: 'Reimbursement Type',
          value: 'reimbursementType',
          sortable: false
        },
        {
          text: 'Month',
          value: 'month',
          sortable: false
        },
        {
          text: 'Bill Date',
          value: 'billDate',
          sortable: false
        },
        {
          text: 'Claim Amount',
          value: 'claimAmount',
          sortable: false
        },
        {
          text: 'Comments',
          value: 'comments',
          sortable: false
        },
        {
          text: 'Attached Bill',
          value: 'attachedBill',
          sortable: false
        },
        {
          text: 'Status',
          value: 'status',
          sortable: false
        }
      ],
      commentDialog: false,
      newComment: '',
      pendingStatusUpdate: null,
      historyDialog: false,
      activeReimbursement: null,
      replyMessage: '',
      replyLoading: false
    }
  },

  methods: {
    viewBill(billPath) {
      // Construct the full URL for the bill image
      const baseUrl = process.env.VUE_APP_S3_BASE_URL || ''
      this.billImageUrl = billPath.startsWith('http') ? billPath : `${baseUrl}${billPath}`
      this.showBillDialog = true
    },
    // getReimbursementTypeName(typeId) {
    //   const type = this.reimbursementTypes?.find((t) => t._id === typeId)
    //   return type ? type.name : 'Unknown Type'
    // },
    getRequestStatuses() {
      // Return statuses formatted for OrderStatuses component
      return this.statuses.map((status) => ({
        label: status,
        value: status
      }))
    },
    initiateStatusUpdate(status, itemId) {
      this.pendingStatusUpdate = { status, itemId }
      this.newComment = ''
      this.commentDialog = true
    },
    cancelStatusUpdate() {
      this.commentDialog = false
      this.pendingStatusUpdate = null
      this.newComment = ''
    },
    async confirmStatusUpdate() {
      if (!this.pendingStatusUpdate) return

      const { status, itemId } = this.pendingStatusUpdate
      try {
        const item = this.reimbursements.find((r) => r._id === itemId)
        const currentApprovals = item && item.approvals ? [...item.approvals] : []

        currentApprovals.push({
          status: status,
          updatedBy: JSON.parse(localStorage.getItem('user'))?._id,
          updatedAt: new Date()
        })

        const payload = {
          approvals: currentApprovals
        }

        if (this.newComment && this.newComment.trim()) {
          const currentComments = item && item.comments ? [...item.comments] : []
          currentComments.push({
            message: this.newComment.trim(),
            commentedAt: new Date(),
            commentedBy: JSON.parse(localStorage.getItem('user'))?._id
          })
          payload.comments = currentComments
        }

        await this.$axios.put(`/orgSetup/reimbursement/${itemId}`, payload)
        this.$emit('statusUpdated')
        this.$toast.success('Status updated successfully')
        this.cancelStatusUpdate()
      } catch (error) {
        console.error('Error updating status:', error)
        this.$toast.error('Failed to update status')
      }
    },
    getCommentsHtml(comments) {
      if (!comments || !comments.length) return ''
      return comments
        .map(
          (c) =>
            `<div style='margin-bottom: 8px; text-align: left; border-bottom: 1px solid #555; padding-bottom: 4px;'>
              <div style='font-weight: bold; font-size: 12px;'>${this.$dayjs(c.commentedAt).format(
                'DD MMM YYYY, hh:mm A'
              )}</div>
              <div style='font-size: 13px;'>${c.message}</div>
            </div>`
        )
        .join('')
    },
    openCommentsDialog(item) {
      this.activeReimbursement = item
      this.replyMessage = ''
      this.historyDialog = true
    },
    closeCommentsDialog() {
      this.historyDialog = false
      this.activeReimbursement = null
      this.replyMessage = ''
    },
    getActiveStatus(item) {
      if (Array.isArray(item?.approvals) && item.approvals.length > 0) {
        return item.approvals[item.approvals.length - 1].status
      }
      return item?.status || 'Pending'
    },
    getStatusChipColor(status) {
      const s = (status || '').toUpperCase()
      if (s === 'APPROVED') return 'green'
      if (s === 'REJECTED') return 'red'
      if (s === 'RESUBMITTED') return 'orange'
      if (s === 'REVIEW') return 'purple'
      return 'blue'
    },
    formatDialogDate(date) {
      if (!date) return ''
      return this.$dayjs(date).format('DD MMM YYYY, hh:mm A')
    },
    async sendQuickReply() {
      if (!this.replyMessage || !this.replyMessage.trim() || !this.activeReimbursement) return
      this.replyLoading = true
      try {
        const msg = this.replyMessage.trim()
        await this.$axios.post(`/orgSetup/reimbursement/${this.activeReimbursement._id}/comment`, {
          message: msg
        })
        const user = JSON.parse(localStorage.getItem('user')) || {}
        const newCommentObj = {
          message: msg,
          commentedAt: new Date(),
          commentedBy: user._id,
          commentedByName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Admin',
          commentedByRole: 'ADMIN'
        }
        if (!Array.isArray(this.activeReimbursement.comments)) {
          this.$set(this.activeReimbursement, 'comments', [])
        }
        this.activeReimbursement.comments.push(newCommentObj)
        this.replyMessage = ''
        this.$toast.success('Message sent')
        this.$emit('statusUpdated')
      } catch (err) {
        console.error('Failed to send comment:', err)
        this.$toast.error('Failed to send comment')
      } finally {
        this.replyLoading = false
      }
    }
  }
}
</script>

<style scoped>
.v-data-table {
  margin-top: 16px;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
